import { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { LoadingPage } from '../LoadingPage'
import * as S from './styles';
import * as G from '../../theme/global-styles';
import ClientLogo from '../../components/ClientLogo';
import PoweredByQITech from '../../components/PoweredByQITech';
import { ThemedButton } from '../../components/ThemedButton';
import { useThemeContext } from "../../contexts/ThemeContext";
import { ErrorPage } from '../ErrorPage';
import ComponentRenderer from '../../components/backend/ComponentRenderer';

export const ContentDisplay = ({
    signData,
    setSignerStep,
    setSignData,
}: {
    signData: any,
    setSignerStep: (step: string) => void,
    setSignData: (data: any) => void,
}) => {

    const { clientThemeSettings } = useThemeContext();

    const [error, setError] = useState({ has_error: false, status: null })
    const [loading, setLoading] = useState(false)
    const [checkboxState, setCheckboxState] = useState<Record<number, boolean>>({});
    const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
    const contentEndRef = useRef<HTMLDivElement>(null)
    const scrollParentRef = useRef<HTMLElement | null>(null)

    useEffect(() => {
        const sentinel = contentEndRef.current;
        if (!sentinel) return;

        const findScrollableAncestor = (el: HTMLElement): HTMLElement => {
            let node = el.parentElement;
            while (node) {
                const { overflowY } = window.getComputedStyle(node);
                if (overflowY === 'auto' || overflowY === 'scroll') return node;
                node = node.parentElement;
            }
            return window as unknown as HTMLElement;
        };

        const scrollParent = findScrollableAncestor(sentinel);
        scrollParentRef.current = scrollParent;

        const THRESHOLD = 50;
        const checkScrollEnd = () => {
            const isWindow = (scrollParent as unknown) === window;
            const nearBottom = isWindow
                ? window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - THRESHOLD
                : scrollParent.scrollTop + scrollParent.clientHeight >= scrollParent.scrollHeight - THRESHOLD;
            if (nearBottom) setHasScrolledToEnd(true);
        };

        scrollParent.addEventListener('scroll', checkScrollEnd, { passive: true });
        checkScrollEnd();
        return () => scrollParent.removeEventListener('scroll', checkScrollEnd);
    }, [loading])

    const content = useMemo(() => signData.content ?? [], [signData.content]);

    const continueDisabled = useMemo(() => {
        return content.some((item: any, index: number) =>
            item.component === 'checkbox' && item.required && !checkboxState[index]
        );
    }, [content, checkboxState]);

    const handleContinue = async () => {
        if (loading || continueDisabled) return;

        const payload = {
            step: "content_display",
            event_date: new Date().toISOString(),
        }

        const config = {
            headers: { Authorization: signData.signer_token },
        }

        setLoading(true);

        try {
            const response = await axios.patch('/signer', payload, config);
            setSignData({ ...signData, ...response.data });
            setSignerStep(response.data.next_step);
        } catch (error) {
            setError({
                has_error: true,
                status: [401, 403, 409].includes((error.response || {}).status)
                    ? error.response.status
                    : 500,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleCheckboxChange = (index: number, checked: boolean) => {
        setCheckboxState(prev => ({ ...prev, [index]: checked }));
    }

    if (loading) {
        return <LoadingPage />
    }

    return (
        <G.Container style={{ padding: "16px 0" }}>
            <ClientLogo clientThemeSettings={clientThemeSettings} size="small" />
            <S.SceneContainer>
                <ComponentRenderer
                    components={content}
                    clientThemeSettings={clientThemeSettings}
                    checkboxState={checkboxState}
                    onCheckboxChange={handleCheckboxChange}
                />
                <div ref={contentEndRef} />
            </S.SceneContainer>
            <S.Footer>
                <S.ButtonContainer>
                    <ThemedButton
                        clientThemeSettings={clientThemeSettings}
                        loading={false}
                        onClick={() => {
                            if (hasScrolledToEnd) {
                                handleContinue();
                            } else {
                                const sp = scrollParentRef.current;
                                if (sp && (sp as unknown) !== window) {
                                    sp.scrollTo({ top: sp.scrollHeight, behavior: 'smooth' });
                                } else {
                                    contentEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                                }
                            }
                        }}
                        disabled={continueDisabled}
                    >
                        Continuar
                    </ThemedButton>
                </S.ButtonContainer>
                <PoweredByQITech />
            </S.Footer>
            {error?.has_error &&
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep("login")}
                    resetError={() => setError({ has_error: false, status: null })}
                    clientThemeSettings={clientThemeSettings}
                />
            }
        </G.Container>
    );
};
